import tenantUtils from '@utils';
import tenantTheme from '@theme';
import tenantConstants from '@constants';
import moment from 'moment';
import { t } from 'i18next';
import { isUnifiedProfolioEnabled } from '../../../utility/user';
import { normalizeSurgeListingItem } from '../../../utility/utility';
import { listingDetailMapper } from './listings';
import { unifyGraphProductTabs } from '../utils/unifiedDashboardListingBreakdownCopy';

const iconMappings = {
  whatsapp: { icon: 'RiWhatsappFill', iconProps: { color: '#25D366', size: '12px' } },
  call: { icon: 'IoCall' },
  phone: { icon: 'IoCall' },
  email: { icon: 'FaEnvelope' },
  manual: { icon: 'FaHandPointer' },
  sms: { icon: 'MdSms' },
  chat: { icon: 'MdOutlineChat', iconProps: { size: '12px' } },
  bayut_match: { icon: 'IconBayutMatch', iconProps: { color: '#28B16D', size: '12px' } },
};

const pickUnifiedLeadPublicUrl = (urlObj, urlL1Obj) => {
  const path =
    urlObj?.bayut ||
    urlObj?.dubizzle ||
    urlObj?.unified ||
    (typeof urlObj === 'string' ? urlObj : null);
  const pathL1 =
    urlL1Obj?.bayut ||
    urlL1Obj?.dubizzle ||
    urlL1Obj?.unified ||
    (typeof urlL1Obj === 'string' ? urlL1Obj : null);
  return { url: path ?? null, url_l1: pathL1 ?? null };
};

/** Matches LMS listing `platforms.*` shape so lead table / ListingPurpose see familiar keys. */
const TRU_LEAD_LISTING_STATUS = { id: 10, name: 'Active', slug: 'active' };
const TRU_LEAD_LISTING_DISPOSITION = {
  id: 42,
  name: 'Live',
  slug: 'live',
  description: null,
  description_l1: null,
};

const isTruthyUrl = (u) => typeof u === 'string' && u.trim().length > 0;

const buildTruLeadPlatformsStub = (item) => {
  const urls = item?.url;
  const urlsL1 = item?.url_l1;
  const leadDate = item?.lead_date;
  const listingId = item?.listing_id;
  const platforms = {};

  const platformBlock = (platformUrl, platformUrlL1) => ({
    url: platformUrl,
    url_l1: platformUrlL1 ?? null,
    is_posted: isTruthyUrl(platformUrl),
    posted_at: leadDate ?? null,
    status: TRU_LEAD_LISTING_STATUS,
    disposition: TRU_LEAD_LISTING_DISPOSITION,
    products_information: {},
    ...(listingId != null && { platform_listing_id: listingId }),
  });

  if (isTruthyUrl(urls?.bayut)) {
    platforms.bayut = platformBlock(urls.bayut, urlsL1?.bayut ?? null);
  }
  if (isTruthyUrl(urls?.dubizzle)) {
    platforms.dubizzle = platformBlock(urls.dubizzle, urlsL1?.dubizzle ?? null);
  }

  return Object.keys(platforms).length ? platforms : undefined;
};

const normalizeTruLeadListingImage = (raw) => {
  if (raw == null) return raw;
  if (typeof raw === 'string') return { thumbnail: raw };
  if (typeof raw === 'object' && raw?.large && !raw.thumbnail && !raw.full) {
    return { ...raw, thumbnail: raw.large, full: raw.large };
  }
  return raw;
};

const buildSyntheticListingFromSurgeTruLead = (item) => {
  const { url, url_l1 } = pickUnifiedLeadPublicUrl(item?.url, item?.url_l1);
  const image = normalizeTruLeadListingImage(item?.image);
  const platforms = buildTruLeadPlatformsStub(item);
  const currency = item?.currency ?? tenantConstants.CURRENCY;

  return {
    id: item?.listing_id,
    title: item?.title,
    title_l1: item?.title_l1,
    beds: item?.beds,
    baths: item?.baths,
    area_unit: item?.area_unit,
    price: item?.price,
    currency,
    location: item?.location,
    image,
    images_count: image ? 1 : item?.images_count ?? 0,
    url,
    url_l1,
    completion_status: item?.location?.classification,
    listing_type: item?.listing_type ?? null,
    ...(item?.listing_purpose && { listing_purpose: item.listing_purpose }),
    unit_variant: item?.unit_variant ?? { floor_plan: [] },
    status: TRU_LEAD_LISTING_STATUS,
    disposition: TRU_LEAD_LISTING_DISPOSITION,
    ad_license: item?.ad_license ?? null,
    permit_number: item?.permit_number ?? null,
    ...(platforms && { platforms }),
  };
};

/** One Surge tru_lead row → LMS `interests[]` element for `leadInterestsMapper`. */
const surgeTruLeadRowToLmsInterestRecord = (item) => {
  const leadType = item?.lead_type || 'chat';
  const leadTypeLabel = leadType.replace(/_/g, ' ');
  const syntheticId = `${item?.buyer_id}::${item?.listing_id}::${item?.lead_date}`;

  return {
    id: syntheticId,
    lead_id: syntheticId,
    recorded_at: item?.lead_date,
    source: leadType,
    source_title: leadTypeLabel,
    source_title_l1: leadTypeLabel,
    message_title: null,
    message_body: item?.message,
    message_body_l1: item?.message,
    recording_url: null,
    call_status: null,
    call_analysis_score: null,
    call_duration: null,
    call_total_duration: null,
    response_time: null,
    project: null,
    listing: buildSyntheticListingFromSurgeTruLead(item),
  };
};

/** buyer_leads `interests[]` row (nested `listing`) → flat row for `surgeTruLeadRowToLmsInterestRecord`. */
const buyerLeadInterestRowToFlatSurgeItem = (interestRow, buyer) => {
  if (!interestRow) return interestRow;
  if (interestRow.listing && buyer && typeof buyer === 'object') {
    return normalizeUnifiedTruLeadsTableRow({
      buyer,
      listing: interestRow.listing,
      lead_date: interestRow.lead_date,
      message: interestRow.message,
      lead_type: interestRow.lead_type,
      lead_count: 1,
      listing_id: interestRow.listing_id,
    });
  }
  return interestRow;
};

/**
 * New unified TruLeads list: each row has `buyer`, `listing`, and assignee on `listing.user`.
 * Legacy row: flat buyer_* + listing_* on one object. Normalize to the flat shape consumed below.
 */
const normalizeUnifiedTruLeadsTableRow = (row) => {
  if (!row) return row;
  const b = row.buyer;
  const L = row.listing;
  if (b && L) {
    return {
      buyer_id: b.buyer_id,
      buyer_name: b.buyer_name,
      buyer_email: b.buyer_email,
      buyer_phone: b.buyer_phone,
      lead_date: row.lead_date,
      message: row.message,
      lead_type: row.lead_type,
      lead_count: row.lead_count,
      listing_id: L.id,
      title: L.title,
      title_l1: L.title_l1,
      beds: L.beds,
      baths: L.baths,
      url: L.url,
      url_l1: L.url_l1,
      listing_type: L.listing_type,
      listing_purpose: L.listing_purpose,
      image: L.image,
      area_unit: L.area_unit,
      location: L.location,
      price: L.price,
      currency: L.currency,
      unit_variant: L.unit_variant,
      images_count: L.images_count,
      ad_license: L.ad_license,
      permit_number: L.permit_number,
      user: L.user,
    };
  }
  return row;
};

/** Map one Surge `tru_leads` row into the LMS lead shape consumed by `leadsDataMapper`. */
const mapSurgeTruLeadRowToLmsLead = (item) => {
  const leadType = item?.lead_type || 'chat';
  const leadTypeLabel = leadType.replace(/_/g, ' ');
  const syntheticLeadId = `${item?.buyer_id}::${item?.listing_id}::${item?.lead_date}`;

  const listingForMapper = buildSyntheticListingFromSurgeTruLead(item);

  const interest = {
    id: syntheticLeadId,
    lead_id: syntheticLeadId,
    source: leadType,
    source_title: leadTypeLabel,
    source_title_l1: leadTypeLabel,
    message_title: null,
    message_body: item?.message,
    message_body_l1: item?.message,
    recorded_at: item?.lead_date,
    recording_url: null,
    call_status: null,
    call_analysis_score: null,
    call_duration: null,
    call_total_duration: null,
    response_time: null,
    project: null,
    listing: listingForMapper,
  };

  return {
    id: syntheticLeadId,
    client: {
      id: item?.buyer_id,
      email: item?.buyer_email,
      mobile: item?.buyer_phone,
      whatsapp: null,
    },
    name: item?.buyer_name,
    name_l1: item?.buyer_name,
    created_at: item?.lead_date,
    updated_at: item?.updated_at ?? item?.lead_date,
    is_viewed: item?.is_viewed ?? true,
    user: item?.user,
    interest,
    task: null,
    interests_count: item?.interests_count ?? 1,
    listings_count: item?.listings_count ?? 1,
    call_leads_count: item?.call_leads_count ?? 0,
    whatsapp_leads_count: item?.whatsapp_leads_count ?? 0,
    email_leads_count: item?.email_leads_count ?? 0,
    sms_leads_count: item?.sms_leads_count ?? 0,
    manual_leads_count: item?.manual_leads_count ?? 0,
  };
};

const tabItems = [
  {
    key: 'calls',
    title: 'Calls',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
  },

  {
    key: 'emails',
    title: 'Emails',
  },
  {
    key: 'sms',
    title: 'SMS',
  },
];

const productTypes = [
  {
    key: 'all',
    title: 'All Listings',
    color: tenantTheme['primary-color'],
  },
  {
    key: 'premium',
    title: 'Basic',
    color: tenantTheme['color-basic'],
    icon: 'IconBasic',
    borderColor: tenantTheme['color-basic'],

    iconProps: {
      color: tenantTheme['color-basic'],
    },
  },
  {
    key: 'hot',
    title: 'Hot',
    icon: 'IconSuperHot',
    color: tenantTheme['color-hot'],
    iconProps: {
      color: tenantTheme['color-hot'],
    },
  },
  {
    key: 'superhot',
    title: 'Signature',
    icon: 'BsFillLightningChargeFill',
    color: tenantTheme['color-signature'],
    iconProps: {
      color: tenantTheme['color-signature'],
    },
  },
];

const graphDataMapper = (leadsStats, user, phoneLeadsStats, labels, graphOptions = {}) => {
  const resolvedProductTypes = unifyGraphProductTabs(productTypes, !!graphOptions.useUnifiedListingGraphLabels);

  const metricMapping = {
    calls: {
      calls_clicked: {
        responseKey: 'sum_phone_view_count',
        borderColor: '#006169',
        label: 'Calls Clicked',
      },
    },
    whatsapp: {
      whatsapp_clicked: {
        responseKey: 'sum_whatsapp_view_count',
        borderColor: '#006169',
        label: 'WhatsApp Clicked',
      },
      ...(user?.is_lms_enabled &&
        user?.is_whatsapp_tracking_enabled &&
        tenantConstants.IS_LMS_ENABLED && {
          whatsapp_sent: {
            responseKey: 'sum_whatsapp_lead_count',
            borderColor: '#28B16D',
            label: 'WhatsApp Sent',
          },
          chats_initiated: {
            responseKey: 'chats_initiated',
            borderColor: '#F7ADA0',
            label: 'Chats Initiated',
          },
        }),
    },
    emails: {
      emails_clicked: {
        responseKey: 'sum_email_view_count',
        label: 'Emails Clicked',
        borderColor: '#006169',
      },
      emails_received: {
        responseKey: 'sum_email_lead_count',
        label: 'Emails Received',
        borderColor: '#009f48',
      },
    },
    sms: {
      sms_clicked: {
        responseKey: 'sum_sms_view_count',
        label: 'SMS Clicked',
        borderColor: '#006169',
      },
    },
  };

  const graphData = {};

  Object.keys(metricMapping).forEach((category) => {
    graphData[category] = {};
    Object.keys(metricMapping[category]).forEach((key) => {
      graphData[category][key] = {
        label: metricMapping?.[category]?.[key]?.label,
        borderColor: metricMapping?.[category]?.[key]?.borderColor,
        ...resolvedProductTypes.reduce((acc, { key: productKey }) => {
          acc[productKey] = [];
          return acc;
        }, {}),
      };
    });
  });

  labels.forEach((dateKey) => {
    Object.keys(metricMapping).forEach((category) => {
      Object.entries(metricMapping[category]).forEach(([subCategoryKey, subCategoryObj]) => {
        graphData[category][subCategoryKey].total = leadsStats?.[subCategoryObj?.responseKey];
        resolvedProductTypes?.forEach((productType) => {
          if (productType?.key == 'all') {
            const value = leadsStats?.items?.[dateKey]?.[subCategoryObj?.responseKey] || 0;
            graphData[category]?.[subCategoryKey]?.[productType?.key]?.push(value);
          } else {
            const value =
              leadsStats?.items?.[dateKey]?.product_wise?.find((product) => product?.ad_product == productType?.key)?.[
                subCategoryObj?.responseKey
              ] || 0;
            graphData[category]?.[subCategoryKey]?.[productType?.key]?.push(value);
          }
        });
      });
    });
  });

  Object.keys(graphData).forEach((key) => {
    if (key == 'calls' && phoneLeadsStats) {
      graphData[key] = { ...graphData?.[key], ...phoneLeadsStats };
    }
  });

  return {
    labels: labels?.map((d) => moment(d)?.locale('en')?.format('MMM DD')),
    data: graphData,
    productTypes: resolvedProductTypes,
    tabItems,
  };
};

const widgetDataMapper = (leadsStats) => {
  return [
    {
      key: 'leads',
      title: 'Total Leads',
      value: leadsStats?.sum_lead_count,
      icon: 'TotalLeads',
      iconProps: { size: '24px' },
    },
    {
      key: 'calls',
      title: 'Calls',
      value: leadsStats?.sum_phone_view_count,
      popoverContent: `${t('Calls Clicked')} ${leadsStats?.sum_phone_view_count}`,
      icon: 'IoCall',
      iconColor: '#479eeb',
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp',
      value: leadsStats?.sum_whatsapp_view_count,
      popoverContent: `${t('WhatsApp Clicked')} ${leadsStats?.sum_whatsapp_view_count}`,
      icon: 'RiWhatsappFill',
      iconColor: '#4CAF50',
    },
    {
      key: 'emails',
      title: 'Emails',
      value: leadsStats?.sum_email_lead_count,
      icon: 'MdMail',
    },
    {
      key: 'sms',
      title: 'SMS',
      value: leadsStats?.sum_sms_view_count,
      icon: 'MdSms',
    },
  ];
};

const leadsDataMapper = (data) => {
  return data?.map((item) => ({
    client_info: {
      id: item?.client.id,
      leadId: item?.id,
      email: item?.client.email,
      phone: item?.client.mobile,
      whatsapp: item?.client.whatsapp,
      name: tenantUtils.getLocalisedString(item, 'name'),
      created_at: item?.created_at,
      createdAt: item?.created_at,
      managedBy: item?.user?.name,
      isViewed: item?.is_viewed,
      source: item?.interest?.source,
    },

    last_interaction: {
      ...iconMappings?.[item?.interest?.source],
      sourceTitle: tenantUtils.getLocalisedString(item?.interest, 'source_title'),
      type: item?.interest?.source,
      content: tenantUtils.getLocalisedString(item?.interest, 'message_body'),
      date: item?.interest?.recorded_at,
      recording_uuid: item?.interest?.recording_url,
      callStatus: item?.interest?.call_status,
      callAnalysisScore: !!item?.interest?.recording_url ? item?.interest?.call_analysis_score : null,
      interestId: item?.interest?.id,
      leadId: item?.id,
      client_info: {
        id: item?.client.id,
        leadId: item?.id,
        email: item?.client.email,
        phone: item?.client.mobile,
        whatsapp: item?.client.whatsapp,
        name: tenantUtils.getLocalisedString(item, 'name'),
        createdAt: item?.created_at,
        managedBy: item?.user?.name,
        isViewed: item?.is_viewed,
        source: item?.interest?.source,
      },
    },

    listing_detail: (() => {
      const listing = normalizeSurgeListingItem(item?.interest?.listing);
      const mapped = listingDetailMapper(listing);
      return {
        ...mapped,
        clientId: item?.client.id,
        leadId: item?.id,
        interests_count: item?.listings_count,
        interestId: item?.interest?.id,
        interest: item?.interest,
        userId: item?.user?.id,
        url: tenantUtils.getLocalisedString(listing, 'url'),
        assignedTo: mapped?.assignedTo || tenantUtils.getLocalisedString(item?.user, 'name') || undefined,
      };
    })(),
    actions: {
      id: item?.id,
      buttonProps: { size: 'small', iconSize: '1em' },
    },
    tasks_detail: {
      parentTask: {
        title: item?.task?.task_purpose?.name,
        title_l1: item?.task?.task_purpose?.name_l1,
        date: item?.task?.completed_at,
        agent_name: item?.task?.assignee?.name,
        agent_name_l1: item?.task?.assignee?.name_l1,
      },
      childTask: item?.task?.child?.id && {
        title: item?.task?.child?.task_purpose?.name,
        title_l1: item?.task?.child?.task_purpose?.name_l1,
        date: item?.task?.child?.created_at,
        isOverdue: item?.task?.child?.due_date ? moment(item.task.child.due_date).isBefore(moment()) : false,
      },
    },
  }));
};

export const interestDetailMapper = (response) => {
  const interest = response?.interest;
  if (!interest) return null;
  const listing = normalizeSurgeListingItem(interest.listing);
  return {
    ...interest,
    listing_detail: listing
      ? {
          ...listingDetailMapper(listing),
          interestId: interest.id,
          interest,
        }
      : undefined,
  };
};

export const leadInterestsMapper = (response) => {
  return {
    list: (response?.interests ?? []).map((item) => {
      const listing = normalizeSurgeListingItem(item?.listing);
      return {
        date: { value: item?.recorded_at, showTime: true, showPopover: false },
        listing_detail: { ...listingDetailMapper(listing), interestId: item?.id, interest: item },
        lead_source: {
          ...iconMappings?.[item?.source],
          sourceTitle: tenantUtils.getLocalisedString(item, 'source_title'),
          type: item?.source,
          content: tenantUtils.getLocalisedString(item, 'message_body'),
          date: item?.recorded_at,
          recording_uuid: item?.recording_url,
          callStatus: item?.call_status,
          callAnalysisScore: !!item?.recording_url ? item?.call_analysis_score : null,
          interestId: item?.id,
          leadId: item?.lead_id,
        },
      };
    }),
    pagination: tenantUtils.getPaginationObject(response?.pagination),
  };
};

/** Surge buyer_leads `leads.leads` counts → `leadDetailMapper`-style cards for Lead Interaction Sources. */
const mapUnifiedBuyerLeadInteractionCountsToSources = (counts) => {
  const c =
    counts && typeof counts === 'object' && !Array.isArray(counts) ? counts : {};
  return [
    {
      title: 'Total Leads',
      value: c.leads ?? 0,
      icon: 'TotalLeads',
      backgroundColor: '#E8F6F9',
      iconColor: '#006169',
    },
    {
      title: 'Call',
      value: c.phone ?? 0,
      icon: 'IoCall',
      backgroundColor: '#E5EEF8',
      iconColor: '#28609F',
    },
    {
      title: 'Whatsapp',
      value: c.whatsapp ?? 0,
      icon: 'RiWhatsappFill',
      backgroundColor: '#F0FAF6',
      iconColor: '#4CAF50',
    },
    {
      title: 'Email',
      value: c.email ?? 0,
      icon: 'FaEnvelope',
      backgroundColor: '#E8F5F6',
      iconColor: '#388B91',
    },
    {
      title: 'SMS',
      value: c.sms ?? 0,
      icon: 'MdSms',
      backgroundColor: '#F2F8FA',
      iconColor: '#55969B',
    },
    {
      title: 'Chat',
      value: c.chat ?? 0,
      icon: 'MdOutlineChat',
      backgroundColor: '#F5F9FC',
      iconColor: '#28609F',
    },
  ];
};

/** Normalize buyer_leads API body: new top-level `buyer`/`interests`/`pagination` + `leads` counts, or legacy all-under-`leads`. */
const normalizeBuyerLeadsApiPayload = (surgePayload) => {
  const empty = { interests: [], buyer: null, pagination: null, counts: {} };
  if (!surgePayload || typeof surgePayload !== 'object') {
    return empty;
  }

  const L = surgePayload.leads;
  const topInterests = surgePayload.interests;

  const isCountsBlock = (obj) =>
    obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    ('phone' in obj ||
      'email' in obj ||
      'whatsapp' in obj ||
      'sms' in obj ||
      'chat' in obj ||
      'leads' in obj);
  const Lobj = L && typeof L === 'object' && !Array.isArray(L) ? L : null;

  if (Array.isArray(topInterests) && Lobj && isCountsBlock(Lobj) && !Array.isArray(Lobj.interests)) {
    return {
      interests: topInterests,
      buyer: surgePayload.buyer ?? null,
      pagination: surgePayload.pagination ?? null,
      counts: Lobj,
    };
  }

  if (Lobj && (Array.isArray(Lobj.interests) || Lobj.buyer != null)) {
    return {
      interests: Array.isArray(Lobj.interests) ? Lobj.interests : [],
      buyer: Lobj.buyer ?? surgePayload.buyer ?? null,
      pagination: surgePayload.pagination ?? Lobj.pagination ?? null,
      counts: isCountsBlock(Lobj.leads) ? Lobj.leads : {},
    };
  }

  return empty;
};

const mapUnifiedBuyerLeadsPayloadToLeadDetail = (normalized) => {
  const { interests, buyer: explicitBuyer } = normalized || {};
  const buyer =
    explicitBuyer && typeof explicitBuyer === 'object' ? explicitBuyer : null;
  const list = Array.isArray(interests) ? interests : [];
  if (!buyer && list.length === 0) {
    return null;
  }

  const sampleInterest = list[0];
  const listing = sampleInterest?.listing;

  const phoneRaw = buyer?.buyer_phone ?? sampleInterest?.buyer_phone;
  const phone =
    phoneRaw == null || String(phoneRaw).trim() === '' ? undefined : String(phoneRaw).trim();

  let earliestLeadDate = null;
  list.forEach((row) => {
    const d = row?.lead_date;
    if (d != null && d !== '') {
      if (!earliestLeadDate || String(d) < String(earliestLeadDate)) {
        earliestLeadDate = d;
      }
    }
  });

  const user = listing?.user ?? sampleInterest?.user;
  const emailRaw = buyer?.buyer_email ?? sampleInterest?.buyer_email;

  return {
    name: buyer?.buyer_name ?? sampleInterest?.buyer_name,
    email: emailRaw == null || String(emailRaw).trim() === '' ? undefined : String(emailRaw).trim(),
    phone,
    whatsapp: undefined,
    createdAt: earliestLeadDate ?? sampleInterest?.lead_date ?? undefined,
    id: buyer?.buyer_id ?? sampleInterest?.buyer_id,
    managedBy: user ? tenantUtils.getLocalisedString(user, 'name') : undefined,
    userId: user?.id,
    isViewed: true,
  };
};

/** `GET api/surge/bi/tru_leads/buyer_leads` → lead drawer interactions tab (same shape as LMS interests). */
export const unifiedBuyerLeadsInterestsMapper = (surgePayload, queryObj = {}) => {
  const normalized = normalizeBuyerLeadsApiPayload(surgePayload);
  const { interests, buyer, pagination: apiPagination, counts } = normalized;

  const page = Number(queryObj?.page) || 1;
  const perPage = Number(queryObj?.per_page) || 20;
  const n = interests.length;

  const paginationPayload =
    apiPagination && typeof apiPagination === 'object'
      ? {
          ...apiPagination,
          per_page: apiPagination.per_page ?? perPage,
        }
      : {
          current_page: page,
          total_pages: Math.max(page, 1),
          total_count: n,
          per_page: perPage,
          from: n ? (page - 1) * perPage + 1 : 0,
          to: n ? (page - 1) * perPage + n : 0,
          next_page: null,
          prev_page: page > 1 ? page - 1 : null,
        };

  const lmsInterests = interests.map((row) =>
    surgeTruLeadRowToLmsInterestRecord(buyerLeadInterestRowToFlatSurgeItem(row, buyer)),
  );

  const interactionSourceLeadSources = mapUnifiedBuyerLeadInteractionCountsToSources(counts);
  const unifiedBuyerLeadDetail = mapUnifiedBuyerLeadsPayloadToLeadDetail(normalized);

  return {
    ...leadInterestsMapper({
      interests: lmsInterests,
      pagination: paginationPayload,
    }),
    interactionSourceLeadSources,
    ...(unifiedBuyerLeadDetail ? { unifiedBuyerLeadDetail } : {}),
  };
};

export const tasksMapper = (response) => {
  return {
    table: [
      {
        title: 'Date Added',
        dataIndex: 'dateAdded',
        key: 'dateAdded',
        component: 'Date',
      },
      {
        title: 'Completed Task',
        dataIndex: 'completedTask',
        key: 'completedTask',
        component: 'TaskDetail',
      },
      {
        title: 'Planned Task',
        dataIndex: 'plannedTask',
        key: 'plannedTask',
        component: 'TaskDetail',
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        component: 'TaskNotes',
      },
    ],
    list: response?.lead_tasks.map((task) => ({
      id: task?.id,
      dateAdded: { value: task?.created_at, showTime: true, showPopover: false },
      completedTask: {
        childTask: {
          date: task?.completed_at,
          showTime: true,
          showPopover: false,
          title: task?.task_purpose?.name,
          title_l1: task?.task_purpose?.name_l1,
        },
      },
      plannedTask: {
        childTask: {
          date: task?.child?.due_date,
          showTime: true,
          showPopover: false,
          title: task?.child?.task_purpose?.name,
          title_l1: task?.child?.task_purpose?.name_l1,
          isOverdue: task?.child?.due_date ? moment(task.child.due_date).isBefore(moment()) : false,
        },
      },
      taskType: task?.task_type,
      notes: {
        value: task?.notes,
        imageUrl: task?.images?.[0]?.full || null,
        imageName: task?.images?.[0]?.filename,
      },
    })),
    pagination: tenantUtils.getPaginationObject(response?.pagination),
  };
};

export const getLeadsMapper = (response) => {
  return {
    list: leadsDataMapper(response?.leads),
    pagination: tenantUtils.getPaginationObject(response?.pagination),
    totalLeads: response?.pagination?.total_count ?? 0,
  };
};

/** TruLeads unified: `GET api/surge/bi/tru_leads` → same table shape as LMS `getLeadsMapper`. */
export const getUnifiedTruLeadsMapper = (response) => {
  const rawNested = Array.isArray(response?.buyers) ? response.buyers : null;
  const rawLegacy = response?.results?.results;
  const raw = rawNested ?? rawLegacy ?? [];
  const normalized = raw.map(normalizeUnifiedTruLeadsTableRow);
  const lmsShaped = normalized.map(mapSurgeTruLeadRowToLmsLead);
  const n = normalized.length;
  const leadCountSum = normalized.reduce((acc, row) => acc + (Number(row?.lead_count) > 0 ? Number(row.lead_count) : 1), 0);
  const paginationPayload =
    response?.pagination ||
    response?.results?.pagination ||
    (n > 0
      ? {
          current_page: 1,
          total_pages: 1,
          total_count: n,
          per_page: n,
          from: 1,
          to: n,
          next_page: null,
          prev_page: null,
        }
      : {
          current_page: 1,
          total_pages: 1,
          total_count: 0,
          per_page: 10,
          from: 0,
          to: 0,
          next_page: null,
          prev_page: null,
        });

  return {
    list: leadsDataMapper(lmsShaped),
    pagination: tenantUtils.getPaginationObject(paginationPayload),
    totalLeads: response?.total_leads ?? response?.results?.total ?? leadCountSum ?? n,
  };
};

export const leadDetailMapper = (data) => {
  const { client } = data;

  return {
    name: tenantUtils.getLocalisedString(data, 'name'),
    email: client?.email,
    phone: client?.mobile,
    whatsapp: client?.whatsapp,
    createdAt: data?.created_at,
    id: client?.id,
    managedBy: tenantUtils.getLocalisedString(data?.user, 'name'),
    userId: data?.user?.id,
    isViewed: data?.is_viewed,
    leadSources: [
      {
        title: 'Call',
        value: data?.call_leads_count,
        icon: 'IoCall',
        backgroundColor: '#E5EEF8',
        iconColor: '#28609F',
      },
      {
        title: 'Whatsapp',
        value: data?.whatsapp_leads_count,
        icon: 'RiWhatsappFill',
        backgroundColor: '#F0FAF6',
        iconColor: '#4CAF50',
      },
      {
        title: 'Email',
        value: data?.email_leads_count,
        icon: 'FaEnvelope',
        backgroundColor: '#E8F5F6',
        iconColor: '#388B91',
      },
      {
        title: 'Manual',
        value: data?.manual_leads_count,
        icon: 'FaHandPointer',
        backgroundColor: '#EAF9F3',
        iconColor: '#2CB270',
      },
    ],
  };
};

export const phoneStatsMapper = (data, dateArray) => {
  const mappedData = {
    calls_received: {
      responseKey: 'received_calls',
      borderColor: '#55969B',
      label: 'Calls Received',
      total: data?.received_calls,
    },
    calls_answered: {
      responseKey: 'answered_calls',
      borderColor: '#28B16D',
      label: 'Calls Answered',
      total: data?.answered_calls,
    },
    calls_missed: {
      responseKey: 'missed_calls',
      borderColor: '#F73131',
      label: 'Calls Missed',
      total: data?.missed_calls,
    },
  };

  Object.keys(mappedData).forEach((categoryKey) => {
    mappedData[categoryKey] = {
      ...mappedData[categoryKey],

      ...productTypes.reduce((acc, { key: productKey }) => {
        acc[productKey] = [];
        return acc;
      }, {}),
    };
  });

  dateArray.map((dateKey) => {
    Object.keys(mappedData).forEach((categoryKey) => {
      productTypes?.forEach((productType) => {
        if (productType?.key == 'all') {
          const value = data?.items?.[dateKey]?.[mappedData?.[categoryKey]?.responseKey] || 0;
          mappedData[categoryKey]['all'].push(value);
        } else {
          const value =
            data?.items?.[dateKey]?.product_wise?.find((product) => product?.ad_product == productType?.key)?.[
              mappedData?.[categoryKey]?.responseKey
            ] || 0;
          mappedData[categoryKey]?.[productType?.key].push(value);
        }
      });
    });
  });

  return mappedData;
};

export const productStatsMapper = (response, selectedUser, phoneStats, dateArray) => {
  return {
    widgetData: widgetDataMapper(response?.data?.stats),
    graphData: graphDataMapper(response?.data?.stats, selectedUser, phoneStats, dateArray, {
      useUnifiedListingGraphLabels: isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' }),
    }),
  };
};

/** Single Surge API for TruLeads unified: shapes response like LMS `stats` for existing graph/widget mappers. */
export const unifiedSurgeTruLeadsGraphMapper = (surgePayload, selectedUser, dateArray) => {
  const results = surgePayload?.stats?.results ?? {};
  const graph = surgePayload?.stats?.graph ?? {};

  const syntheticStats = {
    sum_lead_count: results.leads?.total ?? 0,
    sum_phone_view_count: results.phone?.total ?? 0,
    sum_whatsapp_view_count: results.whatsapp?.total ?? 0,
    sum_whatsapp_lead_count: results.whatsapp?.sent ?? 0,
    chats_initiated: results.whatsapp?.initiated ?? 0,
    sum_sms_view_count: results.sms?.total ?? 0,
    sum_email_view_count: results.email?.total ?? 0,
    sum_email_lead_count: results.email?.sent ?? 0,
    items: {},
  };

  dateArray.forEach((dateKey) => {
    const row = graph[dateKey] || {};
    const phone = row.phone || {};
    const whatsapp = row.whatsapp || {};
    const email = row.email || {};
    syntheticStats.items[dateKey] = {
      sum_lead_count: row.leads?.total ?? 0,
      sum_phone_view_count: phone.total ?? 0,
      sum_whatsapp_view_count: whatsapp.total ?? 0,
      sum_whatsapp_lead_count: whatsapp.sent ?? 0,
      chats_initiated: whatsapp.initiated ?? 0,
      sum_sms_view_count: row.sms?.total ?? 0,
      sum_email_view_count: email.total ?? 0,
      sum_email_lead_count: email.sent ?? 0,
      product_wise: [],
    };
  });

  return {
    widgetData: widgetDataMapper(syntheticStats),
    graphData: graphDataMapper(syntheticStats, selectedUser, null, dateArray, {
      useUnifiedListingGraphLabels: true,
    }),
  };
};

export const transformResponseTimeGraphData = (apiResponse) => {
  if (!apiResponse?.daily_response_rates?.daily_data) {
    return {
      callResponseRates: [],
      whatsappResponseRates: [],
      callAvgResponseTime: [],
      whatsappAvgResponseTime: [],
      labels: [],
      summary: null
    };
  }

  const dailyData = apiResponse?.daily_response_rates?.daily_data;

  return {
    callResponseRates: dailyData?.map(day => day?.calls?.response_rate ?? 0) || [],
    whatsappResponseRates: dailyData?.map(day => day?.whatsapp?.response_rate ?? 0) || [],
    callAvgResponseTime: dailyData?.map(day => day?.calls?.avg_response_time ?? 0) || [],
    whatsappAvgResponseTime: dailyData?.map(day => day?.whatsapp?.avg_response_time ?? 0) || [],
    labels: dailyData?.map(day => day?.date ?? '') || [],
    summary: apiResponse?.daily_response_rates?.summary ?? null
  };
};

export default {
  getLeadsMapper,
  getUnifiedTruLeadsMapper,
  unifiedBuyerLeadsInterestsMapper,
  phoneStatsMapper,
  productStatsMapper,
  unifiedSurgeTruLeadsGraphMapper,
  transformResponseTimeGraphData,
  leadDetailMapper,
  interestDetailMapper,
  leadInterestsMapper,
  tasksMapper,
  leadsDataMapper,
};
